// Care team (Support Inbox) rows for the Email Timeline, STAFFPROJE-95.
//
// Support Inbox mail is not in ClickHouse email_tracking (it is sent through
// Resend and stored in MySQL support_message), so this view does not come from
// /v2/notifications/cms/email/timeline. It reuses the two endpoints the inbox
// already serves: /v2/support/search for the person's tickets, then
// /v2/support/conversations/<uuid> for each thread. Pure helpers live here so
// the filtering and ordering are testable without a DOM or network.

// Same page size as the ClickHouse timeline (goserver api_cms_timeline.go
// QueryLimit = 20), so paging feels the same on every category.
export const CARE_PAGE_SIZE = 20;

// The filter values the goserver timeline accepts (timelineFilterSQL) mapped to
// support_message.direction. One set of dropdown values drives both sources.
const DIRECTION_TO_SUPPORT = { incoming: 'inbound', outgoing: 'outbound' };

const normEmail = (email) =>
  String(email || '')
    .trim()
    .toLowerCase();

// Two searches find a person's tickets, because neither alone finds them all
// (goserver support db.go searchConversationWhere):
// - student_email= / teacher_email= is scoped to tickets classified as that
//   kind of contact (studentConvSQL / teacherConvSQL). It finds tickets linked
//   by student_uuid / teacher_uuid even when written from an older address, but
//   never a 'guest' ticket: every ticket created before contact_type existed
//   (2026-07-16, no backfill), any whose identity lookup failed, and, on a
//   student profile, any from a person who also has a teacher account (the
//   inbound resolver matches teachers first, inbound.go resolveContactIdentity).
// - q= is unscoped and matches contact_email as a contains LIKE, so it finds
//   those guest and teacher-classified tickets by address.
// Both are fuzzy (prefix and contains), so ownsConversation decides which
// returned threads really belong to this person.
export const searchParamSets = (role, email) => [
  { [`${role}_email`]: email, limit: '100' },
  { q: email, limit: '100' }
];

// Union of both searches' conversation hits, first occurrence wins.
export const uniqueHits = (hitLists) => {
  const seen = new Set();
  return hitLists.flat().filter((hit) => {
    if (!hit || !hit.uuid || seen.has(hit.uuid)) return false;
    seen.add(hit.uuid);
    return true;
  });
};

// Keep a thread only when it is really this person's: linked by the account
// uuid for their role (set at create, and nothing in goserver rewrites it, so
// it survives an email change), or its contact email is exactly theirs.
export const ownsConversation = (conversation, { role, uuid, email }) => {
  if (!conversation) return false;
  const linked = role === 'teacher' ? conversation.teacher_uuid : conversation.student_uuid;
  if (uuid && linked === uuid) return true;
  const want = normEmail(email);
  return !!want && normEmail(conversation.contact && conversation.contact.email) === want;
};

// Flattens thread responses ({ conversation, timeline }) into one list of
// message rows, newest first like the ClickHouse timeline. Events (assignment,
// status changes) are dropped: they are not emails. Internal notes are not in
// `timeline` at all, so they never reach Care's student-facing history. System
// messages (auto acknowledgements) are kept: they are real emails the student
// received.
export const careRows = (threads, direction) => {
  const want = DIRECTION_TO_SUPPORT[direction];
  const rows = [];
  (threads || []).forEach((thread) => {
    const conversation = (thread && thread.conversation) || {};
    ((thread && thread.timeline) || []).forEach((item) => {
      if (item.kind !== 'message' || !item.message) return;
      const { message } = item;
      if (want && message.direction !== want) return;
      rows.push({ conversation, message });
    });
  });
  return rows.sort((a, b) => new Date(b.message.created_at) - new Date(a.message.created_at));
};

export const carePage = (rows, page) =>
  rows.slice((page - 1) * CARE_PAGE_SIZE, page * CARE_PAGE_SIZE);

export const careTotalPages = (rows) => Math.max(1, Math.ceil(rows.length / CARE_PAGE_SIZE));
